import React, { useState } from 'react';
import { useGameContext } from '../../context/GameContext';
import { roomScoreApi } from '../../services/roomScoreApi';
import type { RoomScoreData } from '../../services/roomScoreApi';

// Interface for props specific to this enhanced component
interface GameInterfaceEnhancementProps {
  roomId?: string;
  maxPlayers?: number;
  isVisible?: boolean;
  position?: 'overlay' | 'sidebar' | 'bottom' | 'bottom-right';
  frontendRoomId?: string;
  roomInfo?: { roomId?: string };
}

// Convert shared types to display format
const convertSuitToDisplay = (suit: string): string => {
  const suitMap: Record<string, string> = {
    spades: '♠',
    hearts: '♥',
    diamonds: '♦',
    clubs: '♣'
  };
  return suitMap[suit] || suit;
};

const getSuitColor = (suit: string): string => {
  return suit === 'hearts' || suit === 'diamonds' ? '#dc2626' : '#1f2937';
};

const GameInterfaceEnhancement: React.FC<GameInterfaceEnhancementProps> = ({ 
  roomId, 
  maxPlayers = 4,
  isVisible = true,
  position = 'overlay',
  frontendRoomId,
  roomInfo
}) => {
  const {
    currentPlayer,
    players,
    gameState,
    currentPhase,
    isConnected
  } = useGameContext();

  // Add CSS animations for enhanced ranking badges
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes goldShimmer {
        0% { box-shadow: 0 4px 12px rgba(251, 191, 36, 0.5), 0 0 8px rgba(251, 191, 36, 0.3); }
        50% { box-shadow: 0 6px 20px rgba(251, 191, 36, 0.7), 0 0 16px rgba(251, 191, 36, 0.5); }
        100% { box-shadow: 0 4px 12px rgba(251, 191, 36, 0.5), 0 0 8px rgba(251, 191, 36, 0.3); }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // State management for tabs
  const [activeTab, setActiveTab] = useState<'scores' | 'leaderboard' | 'log'>('scores');
  const [isMinimized, setIsMinimized] = useState(true); // Default to minimized
  
  // Room leaderboard state
  const [serverRoomScores, setServerRoomScores] = useState<RoomScoreData | null>(null);
  const [isLoadingScores, setIsLoadingScores] = useState(false);

  // Load room scores from server API on mount
  React.useEffect(() => {
    // Only load if we have a frontend/database room ID (not Colyseus ID)
    if (frontendRoomId) {
      loadRoomScoresFromServer();
    }
  }, [frontendRoomId]);

  // Auto-refresh room scores when game ends OR when a new game starts
  React.useEffect(() => {
    const fetchRoomScores = async () => {
      const isGameEnded = gameState?.status === 'completed' || gameState?.phase === 'game_end';
      const isNewGameStarting = gameState?.status === 'in_progress' && 
                               gameState?.phase === 'bidding' && 
                               (gameState?.gameHistory?.length === 0);
      
      if ((isGameEnded || isNewGameStarting) && frontendRoomId) {
        if (isGameEnded) {
          console.log('🏆 [GameInterfaceEnhancement] Game ended - fetching room scores');
          await new Promise(resolve => setTimeout(resolve, 2000));
        } else {
          console.log('🔄 [GameInterfaceEnhancement] New game starting - refreshing room scores');
          await new Promise(resolve => setTimeout(resolve, 500));
        }
        
        await loadRoomScoresFromServer();
      }
    };
    
    fetchRoomScores();
  }, [gameState?.status, gameState?.phase, gameState?.gameHistory?.length, frontendRoomId]);

  const loadRoomScoresFromServer = async () => {
    if (!frontendRoomId) return; // Only load if we have a database room ID
    
    setIsLoadingScores(true);
    try {
      const scores = await roomScoreApi.getRoomScores(frontendRoomId);
      if (scores) {
        setServerRoomScores(scores);
        console.log('📊 [GameInterfaceEnhancement] Successfully loaded room scores:', scores);
      } else {
        setServerRoomScores(null);
      }
    } catch (error: any) {
      // 404 is expected for new rooms not yet in database - silently ignore
      if (error?.response?.status !== 404) {
        console.error('❌ [GameInterfaceEnhancement] Failed to load room scores:', error);
      }
      setServerRoomScores(null);
    } finally {
      setIsLoadingScores(false);
    }
  };

  // Suppress unused prop warnings - these props may be used by parent components
  void maxPlayers;
  void roomId;
  void roomInfo;

  // Don't render if not connected or not visible
  if (!isConnected || !isVisible) {
    return null;
  }

  // Don't render during certain phases
  if (currentPhase !== 'bidding' && currentPhase !== 'playing' && gameState?.status !== 'in_progress') {
    return null;
  }

  const getPositionStyles = () => {
    switch (position) {
      case 'bottom-right':
        return {
          position: 'absolute' as const,
          bottom: '16px',
          right: '16px',
          zIndex: 1000,
        };
      default:
        return {
          position: 'absolute' as const,
          top: '10px',
          right: '10px',
          zIndex: 1000,
        };
    }
  };

  if (isMinimized) {
    return (
      <div style={getPositionStyles()}>
        <div style={{
          background: 'rgba(0, 0, 0, 0.9)',
          backdropFilter: 'blur(12px)',
          borderRadius: '8px',
          padding: '6px',
          color: 'white',
          fontSize: '0.7rem',
          display: 'flex',
          gap: '4px',
          border: '2px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)'
        }}>
          <button
            onClick={() => {
              setActiveTab('scores');
              setIsMinimized(false);
            }}
            style={{
              background: 'rgba(16, 185, 129, 0.3)',
              border: '1px solid #10b981',
              color: '#10b981',
              fontSize: '0.7rem',
              fontWeight: 'bold',
              padding: '6px 10px',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            📊
          </button>

          <button
            onClick={() => {
              setActiveTab('leaderboard');
              setIsMinimized(false);
            }}
            style={{
              background: 'rgba(251, 191, 36, 0.3)',
              border: '1px solid #fbbf24',
              color: '#fbbf24',
              fontSize: '0.7rem',
              fontWeight: 'bold',
              padding: '6px 10px',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            🏆
          </button>

          <button
            onClick={() => {
              setActiveTab('log');
              setIsMinimized(false);
            }}
            style={{
              background: 'rgba(34, 197, 94, 0.3)',
              border: '1px solid #22c55e',
              color: '#22c55e',
              fontSize: '0.7rem',
              fontWeight: 'bold',
              padding: '6px 10px',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            📜
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      ...getPositionStyles(),
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Tab Content Container */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.95), rgba(31, 41, 55, 0.95))',
        backdropFilter: 'blur(12px)',
        border: '2px solid rgba(255, 255, 255, 0.3)',
        borderBottom: 'none',
        borderRadius: '10px 10px 0 0',
        padding: '12px',
        color: 'white',
        fontSize: '0.8rem',
        minWidth: '300px',
        maxWidth: '340px',
        maxHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5)'
      }}>
        
        {/* Scores Tab Content */}
        {activeTab === 'scores' && (
          <div style={{ 
            flex: 1, 
            overflowY: 'auto', 
            paddingRight: '4px',
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(16, 185, 129, 0.3) transparent'
          }}>
            <h3 style={{ 
              margin: '0 0 12px 0', 
              fontSize: '1rem', 
              color: '#10b981', 
              fontWeight: '700',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              letterSpacing: '0.5px',
              flexDirection: 'column'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '1.2rem' }}>📊</span>
                <span>GAME SCORES</span>
              </div>
            </h3>
            
            {/* Team Scores Section - Enhanced */}
            {currentPhase === 'playing' && gameState?.currentRound?.scoring && (
              <div style={{ marginBottom: '12px' }}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr',
                  gap: '8px',
                  marginBottom: '8px'
                }}>
                  {/* Bidding Team - Enhanced */}
                  <div style={{
                    padding: '8px',
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.25), rgba(16, 185, 129, 0.15))',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    borderRadius: '10px',
                    border: '2px solid rgba(16, 185, 129, 0.4)',
                    textAlign: 'center',
                    boxShadow: '0 3px 10px rgba(16, 185, 129, 0.2)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    {/* Decorative shimmer */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
                      animation: 'shimmer 3s infinite'
                    }} />
                    
                    <div style={{ 
                      fontSize: '0.7rem', 
                      color: '#10b981', 
                      marginBottom: '4px', 
                      fontWeight: '700',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '3px'
                    }}>
                      <span style={{ fontSize: '0.8rem' }}>🎯</span>
                      <span>BIDDING</span>
                    </div>
                    <div style={{ 
                      fontSize: '1.6rem', 
                      fontWeight: '900', 
                      color: '#10b981',
                      textShadow: '0 3px 6px rgba(16, 185, 129, 0.4), 0 1px 2px rgba(0, 0, 0, 0.3)',
                      marginBottom: '3px',
                      letterSpacing: '0.5px',
                      lineHeight: '1'
                    }}>
                      {gameState.currentRound.scoring.biddingTeam?.pointsWon || 0}
                    </div>
                    <div style={{ 
                      fontSize: '0.7rem', 
                      color: 'rgba(16, 185, 129, 0.9)',
                      fontWeight: '700',
                      marginBottom: '6px',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase'
                    }}>points</div>
                    {/* Show bid info */}
                    {gameState.currentRound?.biddingPhase?.currentBid && (
                      <div style={{
                        fontSize: '0.65rem',
                        color: 'rgba(16, 185, 129, 0.9)',
                        padding: '3px 6px',
                        background: 'rgba(16, 185, 129, 0.15)',
                        borderRadius: '4px',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        display: 'inline-block'
                      }}>
                        Bid: {gameState.currentRound.biddingPhase.currentBid.amount} {convertSuitToDisplay(gameState.currentRound.biddingPhase.currentBid.powerHouse)}
                      </div>
                    )}
                  </div>

                  {/* Opposing Team - Enhanced */}
                  <div style={{
                    padding: '8px',
                    background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.25), rgba(239, 68, 68, 0.15))',
                    backdropFilter: 'blur(8px)',
                    WebkitBackdropFilter: 'blur(8px)',
                    borderRadius: '10px',
                    border: '2px solid rgba(239, 68, 68, 0.4)',
                    textAlign: 'center',
                    boxShadow: '0 3px 10px rgba(239, 68, 68, 0.2)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    {/* Decorative shimmer */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '-100%',
                      width: '100%',
                      height: '100%',
                      background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent)',
                      animation: 'shimmer 3s infinite'
                    }} />
                    
                    <div style={{ 
                      fontSize: '0.7rem', 
                      color: '#ef4444', 
                      marginBottom: '4px', 
                      fontWeight: '700',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '3px'
                    }}>
                      <span style={{ fontSize: '0.8rem' }}>🛡️</span>
                      <span>OPPOSING</span>
                    </div>
                    <div style={{ 
                      fontSize: '1.6rem', 
                      fontWeight: '900', 
                      color: '#ef4444',
                      textShadow: '0 3px 6px rgba(239, 68, 68, 0.4), 0 1px 2px rgba(0, 0, 0, 0.3)',
                      marginBottom: '3px',
                      letterSpacing: '0.5px',
                      lineHeight: '1'
                    }}>
                      {gameState.currentRound.scoring.opposingTeam?.pointsWon || 0}
                    </div>
                    <div style={{ 
                      fontSize: '0.7rem', 
                      color: 'rgba(239, 68, 68, 0.9)',
                      fontWeight: '700',
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase'
                    }}>points</div>
                  </div>
                </div>

                {/* Team Player Lists - Enhanced & Compact */}
                <div style={{ 
                  display: 'flex', 
                  gap: '8px', 
                  marginTop: '8px'
                }}>
                  {/* Bidding Team Players - Enhanced */}
                  <div style={{
                    flex: 1,
                    padding: '8px',
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(16, 185, 129, 0.08))',
                    backdropFilter: 'blur(6px)',
                    WebkitBackdropFilter: 'blur(6px)',
                    borderRadius: '8px',
                    border: '2px solid rgba(16, 185, 129, 0.3)',
                    boxShadow: '0 2px 6px rgba(16, 185, 129, 0.15)'
                  }}>
                    <div style={{ 
                      fontSize: '0.7rem', 
                      color: '#10b981', 
                      marginBottom: '6px', 
                      fontWeight: '700',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px'
                    }}>
                      <span style={{ fontSize: '0.75rem' }}>👥</span>
                      <span>BIDDING</span>
                    </div>
                    {/* Show bidder */}
                    {(() => {
                      const bidder = gameState.currentRound ? players.find(p => p.id === gameState.currentRound?.scoring.biddingTeam?.bidder) : null;
                      return bidder ? (
                        <div style={{
                          fontSize: '0.75rem',
                          color: '#e5e7eb',
                          padding: '4px 6px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          background: 'rgba(16, 185, 129, 0.1)',
                          borderRadius: '5px',
                          marginBottom: '3px',
                          border: '1px solid rgba(16, 185, 129, 0.2)'
                        }}>
                          <span style={{ fontWeight: '600' }}>
                            {bidder.name}
                            {bidder.id === currentPlayer?.id && ' 👤'}
                          </span>
                        </div>
                      ) : null;
                    })()}
                    {/* Show all revealed partners from playPhase */}
                    {gameState.currentRound?.playPhase?.revealedPartners && 
                     gameState.currentRound.playPhase.revealedPartners.length > 0 && 
                     gameState.currentRound.playPhase.revealedPartners.map((revealedPartner: any) => {
                      const partnerId = typeof revealedPartner === 'string' ? revealedPartner : revealedPartner.playerId;
                      const partner = players.find(p => p.id === partnerId);
                      return partner ? (
                        <div key={partnerId} style={{
                          fontSize: '0.75rem',
                          color: '#e5e7eb',
                          padding: '4px 6px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          background: 'rgba(16, 185, 129, 0.1)',
                          borderRadius: '5px',
                          marginBottom: '3px',
                          border: '1px solid rgba(16, 185, 129, 0.2)'
                        }}>
                          <span style={{ fontWeight: '600' }}>
                            {partner.name}
                            {partner.id === currentPlayer?.id && ' 👤'}
                          </span>
                        </div>
                      ) : null;
                    })}
                  </div>

                  {/* Opposing Team Players - Enhanced */}
                  <div style={{
                    flex: 1,
                    padding: '8px',
                    background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(239, 68, 68, 0.08))',
                    backdropFilter: 'blur(6px)',
                    WebkitBackdropFilter: 'blur(6px)',
                    borderRadius: '8px',
                    border: '2px solid rgba(239, 68, 68, 0.3)',
                    boxShadow: '0 2px 6px rgba(239, 68, 68, 0.15)'
                  }}>
                    <div style={{ 
                      fontSize: '0.7rem', 
                      color: '#ef4444', 
                      marginBottom: '6px', 
                      fontWeight: '700',
                      letterSpacing: '0.5px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '3px'
                    }}>
                      <span style={{ fontSize: '0.75rem' }}>👥</span>
                      <span>OPPOSING</span>
                    </div>
                    {gameState.currentRound.scoring.opposingTeam?.players.map((memberId: string) => {
                      const member = players.find(p => p.id === memberId);
                      return member ? (
                        <div key={memberId} style={{
                          fontSize: '0.75rem',
                          color: '#e5e7eb',
                          padding: '4px 6px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          background: 'rgba(239, 68, 68, 0.1)',
                          borderRadius: '5px',
                          marginBottom: '3px',
                          border: '1px solid rgba(239, 68, 68, 0.2)'
                        }}>
                          <span style={{ fontWeight: '600' }}>
                            {member.name}
                            {member.id === currentPlayer?.id && ' 👤'}
                          </span>
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Individual Player Scores * s}
            {players.length >= 4 ? (
              <div style={{ display: 'grid', gap: '6px' }}>
                {players.map((player, index) => (
                  <div
                    key={player.id}
                    style={{
                      padding: '6px 8px',
                      background: player.id === currentPlayer?.id
                        ? 'rgba(16, 185, 129, 0.2)'
                        : 'rgba(255, 255, 255, 0.05)',
                      borderRadius: '6px',
                      border: player.id === currentPlayer?.id
                        ? '1px solid #10b981'
                        : '1px solid rgba(255, 255, 255, 0.1)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <div style={{ 
                        fontSize: '0.7rem', 
                        color: 'rgba(255, 255, 255, 0.5)',
                        minWidth: '12px'
                      }}>
                        {index + 1}
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '0.8rem' }}>
                          {player.name}
                          {player.id === currentPlayer?.id && (
                            <span style={{ marginLeft: '4px', fontSize: '0.7rem' }}>👤</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#ffffff' }}>
                      {((gameState as any)?.cumulativePlayerScores?.[player.id] || 0)} pts
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: '#9ca3af', padding: '12px', fontSize: '0.75rem' }}>
                Waiting for players to join...
              </div>
            )}
            */}
          </div>
        )}

        {/* Leaderboard Tab Content */}
        {activeTab === 'leaderboard' && (
          <div style={{ 
            flex: 1, 
            overflowY: 'auto', 
            paddingRight: '4px',
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(251, 191, 36, 0.3) transparent'
          }}>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '0.9rem', color: '#fbbf24', fontWeight: 'bold' }}>
              🏆 Room Leaderboard
            </h3>
            
            {isLoadingScores ? (
              <div style={{ textAlign: 'center', color: '#9ca3af', padding: '12px', fontSize: '0.75rem' }}>
                Loading...
              </div>
            ) : serverRoomScores && serverRoomScores.scores && serverRoomScores.scores.length > 0 ? (
              <div>
                {/* Room Progress Info */}
                <div style={{
                  padding: '6px 8px',
                  background: 'rgba(251, 191, 36, 0.15)',
                  borderRadius: '6px',
                  border: '1px solid rgba(251, 191, 36, 0.3)',
                  marginBottom: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#ffffff' }}>
                    {serverRoomScores.gamesPlayed} Games Played
                  </div>
                </div>

                {/* Player Rankings */}
                <div style={{ display: 'grid', gap: '6px' }}>
                  {serverRoomScores.scores
                    .sort((a: any, b: any) => b.cumulativeScore - a.cumulativeScore)
                    .map((player: any, index: number) => (
                    <div
                      key={player.playerId}
                      style={{
                        padding: '6px 8px',
                        background: index === 0 
                          ? 'rgba(16, 185, 129, 0.2)' 
                          : 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '6px',
                        border: index === 0 
                          ? '1px solid #10b981' 
                          : '1px solid rgba(255, 255, 255, 0.1)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {index === 0 && (
                          <div style={{
                            background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                            borderRadius: '12px',
                            padding: '6px 10px',
                            fontSize: '1.4rem',
                            fontWeight: '900',
                            color: '#000000',
                            textShadow: 'none',
                            boxShadow: '0 4px 12px rgba(251, 191, 36, 0.5), 0 0 8px rgba(251, 191, 36, 0.3)',
                            animation: 'goldShimmer 3s infinite ease-in-out',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            minWidth: '60px',
                            justifyContent: 'center'
                          }}>
                            👑 1ST
                          </div>
                        )}
                        {index === 1 && (
                          <div style={{
                            background: 'linear-gradient(135deg, #9ca3af, #6b7280)',
                            borderRadius: '10px',
                            padding: '5px 8px',
                            fontSize: '1.2rem',
                            fontWeight: '800',
                            color: '#ffffff',
                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                            boxShadow: '0 3px 8px rgba(156, 163, 175, 0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '3px',
                            minWidth: '52px',
                            justifyContent: 'center'
                          }}>
                            🥈 2ND
                          </div>
                        )}
                        {index === 2 && (
                          <div style={{
                            background: 'linear-gradient(135deg, #cd7c32, #b45309)',
                            borderRadius: '8px',
                            padding: '4px 7px',
                            fontSize: '1rem',
                            fontWeight: '700',
                            color: '#ffffff',
                            textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
                            boxShadow: '0 2px 6px rgba(205, 124, 50, 0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '2px',
                            minWidth: '44px',
                            justifyContent: 'center'
                          }}>
                            🥉 3RD
                          </div>
                        )}
                        {index > 2 && (
                          <div style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            borderRadius: '6px',
                            padding: '3px 6px',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            color: 'rgba(255,255,255,0.7)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            minWidth: '32px',
                            textAlign: 'center'
                          }}>
                            {index + 1}
                          </div>
                        )}
                        <div>
                          <div style={{ 
                            fontSize: '0.8rem', 
                            fontWeight: '600', 
                            color: index === 0 ? '#10b981' : '#ffffff' 
                          }}>
                            {player.playerName}
                          </div>
                          <div style={{ fontSize: '0.65rem', color: '#9ca3af' }}>
                            {player.gamesWon}W • {player.gamesPlayed}G
                          </div>
                        </div>
                      </div>
                      
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ 
                          fontSize: '0.85rem', 
                          fontWeight: 'bold', 
                          color: player.cumulativeScore > 0 ? '#10b981' : 
                                 player.cumulativeScore < 0 ? '#ef4444' : '#ffffff'
                        }}>
                          {player.cumulativeScore > 0 && '+'}
                          {player.cumulativeScore}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: '#9ca3af', padding: '12px', fontSize: '0.75rem' }}>
                <div>No games completed yet</div>
              </div>
            )}
          </div>
        )}

        {/* Log Tab Content */}
        {activeTab === 'log' && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            flex: 1,
            minHeight: 0
          }}>
            {/* Fixed Header */}
            <div style={{ 
              margin: '0 0 12px 0', 
              fontSize: '1rem', 
              color: '#22c55e', 
              fontWeight: 'bold',
              flexShrink: 0,
              paddingBottom: '8px',
              borderBottom: '2px solid rgba(34, 197, 94, 0.3)'
            }}>
              📜 Game Log
              {roomInfo?.roomId && (
                <div style={{
                  fontSize: '0.7rem',
                  color: '#9ca3af',
                  fontWeight: '500',
                  marginTop: '4px',
                  letterSpacing: '0.5px'
                }}>
                  Room: {roomInfo.roomId}
                </div>
              )}
            </div>
            
            {/* Scrollable Content */}
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '12px', 
              flex: 1,
              overflowY: 'auto',
              minHeight: 0,
              paddingRight: '4px'
            }}
            className="game-log-scroll">

              {/* Debug Info - Remove this in production */}
              {process.env.NODE_ENV === 'development' && (
                <div style={{ 
                  padding: '6px', 
                  background: 'rgba(59, 130, 246, 0.1)', 
                  borderRadius: '4px', 
                  fontSize: '0.65rem', 
                  color: '#9ca3af',
                  border: '1px solid rgba(59, 130, 246, 0.2)'
                }}>
                  DEBUG - Phase: {currentPhase}, GameHistory: {gameState?.gameHistory?.length || 0}, 
                  CurrentRound: {gameState?.currentRound ? 'Yes' : 'No'}, 
                  CompletedTricks: {gameState?.currentRound?.playPhase?.completedTricks?.length || 0}
                </div>
              )}
              {/* Complete Game History - All Completed Rounds */}
              {gameState?.gameHistory && gameState.gameHistory.length > 0 && (
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#3b82f6', marginBottom: '8px', fontWeight: 'bold' }}>
                    🏆 Completed Rounds ({gameState.gameHistory.length})
                  </div>
                  {gameState.gameHistory.map((round: any, roundIndex: number) => (
                    <div key={roundIndex} style={{
                      marginBottom: '12px',
                      padding: '10px',
                      background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2), rgba(59, 130, 246, 0.1))',
                      borderRadius: '8px',
                      border: '2px solid rgba(59, 130, 246, 0.3)',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      maxWidth: '100%'
                    }}>
                      <div style={{ 
                        fontSize: '0.8rem', 
                        color: '#60a5fa', 
                        fontWeight: 'bold', 
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span>🎲</span>
                        <span>Round {roundIndex + 1}</span>
                        {round.scoring && (
                          <span style={{ 
                            fontSize: '0.7rem', 
                            color: '#9ca3af',
                            marginLeft: 'auto'
                          }}>
                            {round.playPhase?.completedTricks?.length || 0} tricks
                          </span>
                        )}
                      </div>

                      {/* Bidding History for this round */}
                      {round.biddingPhase?.biddingHistory && round.biddingPhase.biddingHistory.length > 0 && (
                        <div style={{ marginBottom: '8px' }}>
                          <div style={{ fontSize: '0.75rem', color: '#fbbf24', marginBottom: '4px', fontWeight: 'bold' }}>
                            💰 Bidding Phase
                          </div>
                          {round.biddingPhase.biddingHistory.map((entry: any, bidIndex: number) => {
                            const player = players.find(p => p.id === entry.playerId);
                            const playerName = player?.name || 'Unknown';
                            
                            return (
                              <div key={bidIndex} style={{ 
                                fontSize: '0.75rem', 
                                color: '#d1d5db', 
                                marginBottom: '2px',
                                padding: '4px 6px',
                                background: 'rgba(0, 0, 0, 0.2)',
                                borderRadius: '4px'
                              }}>
                                {entry.amount === 0 ? (
                                  <span style={{ color: '#fca5a5' }}>
                                    🚫 {playerName} passed
                                  </span>
                                ) : (
                                  <span style={{ color: '#86efac' }}>
                                    💰 {playerName}: {entry.amount} 
                                    <span style={{ color: getSuitColor(entry.powerHouse), marginLeft: '4px' }}>
                                      {convertSuitToDisplay(entry.powerHouse)}
                                    </span>
                                  </span>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Completed Tricks for this round */}
                      {round.playPhase?.completedTricks && round.playPhase.completedTricks.length > 0 && (
                        <div>
                          <div style={{ fontSize: '0.75rem', color: '#22c55e', marginBottom: '4px', fontWeight: 'bold' }}>
                            🃏 Tricks Played ({round.playPhase.completedTricks.length})
                          </div>
                          {round.playPhase.completedTricks.map((trick: any, trickIndex: number) => (
                            <div key={trickIndex} style={{
                              marginBottom: '6px',
                              padding: '6px 8px',
                              background: 'rgba(34, 197, 94, 0.15)',
                              borderRadius: '6px',
                              border: '1px solid rgba(34, 197, 94, 0.3)'
                            }}>
                              <div style={{ 
                                fontSize: '0.7rem', 
                                color: '#86efac', 
                                fontWeight: 'bold',
                                marginBottom: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                <span>Trick {trickIndex + 1}</span>
                                {trick.winner && (
                                  <span style={{ color: '#fbbf24', marginLeft: 'auto' }}>
                                    👑 {players.find(p => p.id === trick.winner)?.name || 'Unknown'}
                                  </span>
                                )}
                              </div>
                              {trick.cards && trick.cards.map((playedCard: any, cardIndex: number) => {
                                const cardPlayer = players.find(p => p.id === playedCard.playerId);
                                const isWinner = trick.winner === playedCard.playerId;
                                return (
                                  <div key={cardIndex} style={{ 
                                    fontSize: '0.7rem', 
                                    color: isWinner ? '#fbbf24' : '#d1d5db',
                                    marginBottom: '2px',
                                    fontWeight: isWinner ? 'bold' : 'normal',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}>
                                    {isWinner && <span>👑</span>}
                                    <span>{cardPlayer?.name}:</span>
                                    <span>{playedCard.card.rank}</span>
                                    <span style={{ color: getSuitColor(playedCard.card.suit), fontSize: '0.8rem' }}>
                                      {convertSuitToDisplay(playedCard.card.suit)}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Round Scoring */}
                      {round.scoring && (
                        <div style={{
                          marginTop: '8px',
                          padding: '6px 8px',
                          background: 'rgba(251, 191, 36, 0.15)',
                          borderRadius: '6px',
                          border: '1px solid rgba(251, 191, 36, 0.3)'
                        }}>
                          <div style={{ fontSize: '0.75rem', color: '#fbbf24', fontWeight: 'bold', marginBottom: '4px' }}>
                            📊 Round Results
                          </div>
                          {round.scoring.individualScores && Object.entries(round.scoring.individualScores).map(([playerId, score]: [string, any]) => {
                            const player = players.find(p => p.id === playerId);
                            return (
                              <div key={playerId} style={{
                                fontSize: '0.7rem',
                                color: '#d1d5db',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: '2px'
                              }}>
                                <span>{player?.name || 'Unknown'}</span>
                                <span style={{
                                  color: score > 0 ? '#10b981' : score < 0 ? '#ef4444' : '#9ca3af',
                                  fontWeight: 'bold'
                                }}>
                                  {score > 0 ? '+' : ''}{score} pts
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Current Round Bidding History */}
              {gameState?.currentRound?.biddingPhase.biddingHistory && gameState.currentRound.biddingPhase.biddingHistory.length > 0 && (
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#fbbf24', marginBottom: '8px', fontWeight: 'bold' }}>
                    💰 Current Round Bidding
                  </div>
                  {gameState.currentRound.biddingPhase.biddingHistory.map((entry: any, index: number) => {
                    const player = players.find(p => p.id === entry.playerId);
                    const playerName = player?.name || 'Unknown';
                    
                    return (
                      <div key={index} style={{ 
                        marginBottom: '6px', 
                        padding: '8px',
                        background: entry.amount === 0 ? 'rgba(239, 68, 68, 0.2)' : 'rgba(34, 197, 94, 0.2)',
                        borderRadius: '6px',
                        borderLeft: `3px solid ${entry.amount === 0 ? '#ef4444' : '#22c55e'}`,
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        maxWidth: '100%'
                      }}>
                        {entry.amount === 0 ? (
                          <span style={{ color: '#fca5a5', fontSize: '0.8rem' }}>
                            🚫 {playerName} passed
                          </span>
                        ) : (
                          <div>
                            <div style={{ color: '#86efac', fontWeight: 'bold', fontSize: '0.8rem' }}>
                              💰 {playerName} bid {entry.amount} points
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#d1d5db', marginTop: '2px' }}>
                              Power: <span style={{ color: getSuitColor(entry.powerHouse) }}>
                                {convertSuitToDisplay(entry.powerHouse)}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Current Round Completed Tricks */}
              {currentPhase === 'playing' && gameState?.currentRound?.playPhase?.completedTricks && gameState.currentRound.playPhase.completedTricks.length > 0 && (
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#22c55e', marginBottom: '8px', fontWeight: 'bold' }}>
                    🃏 Current Round Tricks ({gameState.currentRound.playPhase.completedTricks.length})
                  </div>
                  {gameState.currentRound.playPhase.completedTricks.map((trick: any, trickIndex: number) => (
                    <div key={trickIndex} style={{
                      marginBottom: '8px',
                      padding: '8px',
                      background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.2), rgba(34, 197, 94, 0.1))',
                      borderRadius: '8px',
                      border: '2px solid rgba(34, 197, 94, 0.3)',
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      maxWidth: '100%'
                    }}>
                      <div style={{ 
                        fontSize: '0.75rem', 
                        color: '#86efac', 
                        fontWeight: 'bold',
                        marginBottom: '6px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}>
                        <span>🎯 Trick {trickIndex + 1}</span>
                        {trick.winner && (
                          <span style={{ color: '#fbbf24', marginLeft: 'auto', fontSize: '0.7rem' }}>
                            👑 {players.find(p => p.id === trick.winner)?.name || 'Unknown'}
                          </span>
                        )}
                      </div>
                      {trick.cards && trick.cards.map((playedCard: any, cardIndex: number) => {
                        const cardPlayer = players.find(p => p.id === playedCard.playerId);
                        const isWinner = trick.winner === playedCard.playerId;
                        return (
                          <div key={cardIndex} style={{ 
                            fontSize: '0.75rem', 
                            color: isWinner ? '#fbbf24' : '#d1d5db',
                            marginBottom: '3px',
                            fontWeight: isWinner ? 'bold' : 'normal',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            padding: '2px 4px',
                            background: isWinner ? 'rgba(251, 191, 36, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '4px'
                          }}>
                            {isWinner && <span style={{ fontSize: '0.65rem' }}>👑</span>}
                            <span style={{ minWidth: '60px' }}>{cardPlayer?.name}:</span>
                            <span style={{ fontWeight: 'bold' }}>{playedCard.card.rank}</span>
                            <span style={{ color: getSuitColor(playedCard.card.suit), fontSize: '0.85rem' }}>
                              {convertSuitToDisplay(playedCard.card.suit)}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              )}

              {/* Current Trick in Progress */}
              {currentPhase === 'playing' && gameState?.currentRound?.playPhase?.currentTrick?.cards && gameState.currentRound.playPhase.currentTrick.cards.length > 0 && (
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#fbbf24', marginBottom: '8px', fontWeight: 'bold' }}>
                    🎯 Current Trick
                  </div>
                  <div style={{ 
                    padding: '8px',
                    background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.2), rgba(251, 191, 36, 0.1))',
                    borderRadius: '8px',
                    border: '2px solid rgba(251, 191, 36, 0.4)',
                    maxWidth: '100%',
                    overflow: 'hidden'
                  }}>
                    {gameState.currentRound.playPhase.currentTrick.cards.map((playedCard: any, cardIndex: number) => {
                      const cardPlayer = players.find(p => p.id === playedCard.playerId);
                      return (
                        <div key={cardIndex} style={{ 
                          fontSize: '0.75rem', 
                          color: '#d1d5db',
                          marginBottom: '3px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '2px 4px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '4px',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word'
                        }}>
                          <span style={{ minWidth: '60px' }}>{cardPlayer?.name}:</span>
                          <span style={{ fontWeight: 'bold' }}>{playedCard.card.rank}</span>
                          <span style={{ color: getSuitColor(playedCard.card.suit), fontSize: '0.85rem' }}>
                            {convertSuitToDisplay(playedCard.card.suit)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* No History Available */}
              {(!gameState?.gameHistory || gameState.gameHistory.length === 0) && 
               (!gameState?.currentRound?.biddingPhase.biddingHistory || gameState.currentRound.biddingPhase.biddingHistory.length === 0) && 
               (!currentPhase || currentPhase !== 'playing' || !gameState?.currentRound?.playPhase?.currentTrick?.cards || gameState.currentRound.playPhase.currentTrick.cards.length === 0) && (
                <div style={{ 
                  color: '#9ca3af', 
                  fontStyle: 'italic', 
                  fontSize: '0.8rem',
                  textAlign: 'center',
                  padding: '20px',
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  border: '1px dashed rgba(255, 255, 255, 0.2)'
                }}>
                  🎯 Game log will appear here as rounds are played...
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Your Turn Indicator - displayed above tabs during playing phase 
      {currentPhase === 'playing' && 
       gameState?.currentRound?.playPhase?.currentPlayer === currentPlayer?.id && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.4), rgba(16, 185, 129, 0.4))',
          border: '2px solid #10b981',
          borderRadius: '8px 8px 0 0',
          padding: '8px 12px',
          marginTop: '8px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          boxShadow: '0 0 20px rgba(16, 185, 129, 0.6), 0 0 40px rgba(16, 185, 129, 0.3)',
          transition: 'all 0.3s ease-in-out',
          maxWidth: '340px',
          width: '100%'
        }}>
          <span style={{ fontSize: '1.2rem' }}>⏰</span>
          <span style={{ 
            fontSize: '0.9rem', 
            fontWeight: 'bold', 
            color: '#10b981',
            textTransform: 'uppercase',
            letterSpacing: '1px',
            textShadow: '0 0 10px rgba(16, 185, 129, 0.8)'
          }}>
            Your Turn
          </span>
          <span style={{ fontSize: '1.2rem' }}>⏰</span>
        </div> 
      )}
*/}
      {/* Tab Navigation - Now at bottom */}
      <div style={{
        display: 'flex',
        gap: '2px',
        background: 'rgba(0, 0, 0, 0.9)',
        backdropFilter: 'blur(12px)',
        borderRadius: '0 0 10px 10px',
        padding: '0 8px 8px 8px',
        border: '2px solid rgba(255, 255, 255, 0.3)',
        borderTop: 'none',
        maxWidth: '340px',
        width: '100%'
      }}>
        <button
          onClick={() => setActiveTab('scores')}
          style={{
            background: activeTab === 'scores' ? 'rgba(16, 185, 129, 0.4)' : 'transparent',
            border: 'none',
            borderTop: activeTab === 'scores' ? '3px solid #10b981' : '3px solid transparent',
            color: activeTab === 'scores' ? '#10b981' : '#9ca3af',
            fontSize: '0.75rem',
            fontWeight: 'bold',
            padding: '10px 18px',
            borderRadius: '0 0 6px 6px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          📊 Scores
        </button>

        <button
          onClick={() => setActiveTab('leaderboard')}
          style={{
            background: activeTab === 'leaderboard' ? 'rgba(251, 191, 36, 0.4)' : 'transparent',
            border: 'none',
            borderTop: activeTab === 'leaderboard' ? '3px solid #fbbf24' : '3px solid transparent',
            color: activeTab === 'leaderboard' ? '#fbbf24' : '#9ca3af',
            fontSize: '0.75rem',
            fontWeight: 'bold',
            padding: '10px 18px',
            borderRadius: '0 0 6px 6px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          🏆 Leaderboard
        </button>

        <button
          onClick={() => setActiveTab('log')}
          style={{
            background: activeTab === 'log' ? 'rgba(34, 197, 94, 0.4)' : 'transparent',
            border: 'none',
            borderTop: activeTab === 'log' ? '3px solid #22c55e' : '3px solid transparent',
            color: activeTab === 'log' ? '#22c55e' : '#9ca3af',
            fontSize: '0.75rem',
            fontWeight: 'bold',
            padding: '10px 18px',
            borderRadius: '0 0 6px 6px',
            cursor: 'pointer',
            transition: 'all 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '6px'
          }}
        >
          📜 Log
        </button>

        <button
          onClick={() => setIsMinimized(true)}
          style={{
            background: 'none',
            border: 'none',
            color: '#9ca3af',
            fontSize: '0.75rem',
            cursor: 'pointer',
            marginLeft: 'auto',
            padding: '10px 14px',
            transition: 'color 0.2s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.color = '#ffffff'}
          onMouseLeave={(e) => e.currentTarget.style.color = '#9ca3af'}
        >
          ➖
        </button>
      </div>

      {/* Status Footer */}
      <div style={{
        background: 'rgba(0, 0, 0, 0.9)',
        backdropFilter: 'blur(12px)',
        borderRadius: '0 0 10px 10px',
        padding: '8px 12px',
        color: 'white',
        fontSize: '0.7rem',
        textAlign: 'center',
        border: '2px solid rgba(255, 255, 255, 0.3)',
        borderTop: 'none',
        marginTop: '-2px'
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <span style={{ color: isConnected ? '#10b981' : '#ef4444' }}>
            {isConnected ? '🟢 Connected' : '🔴 Disconnected'}
          </span>
          {roomInfo?.roomId && (
            <>
              <span style={{ color: '#9ca3af' }}>•</span>
              <span style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                ID: {roomInfo.roomId}
              </span>
            </>
          )}
          <span style={{ color: '#9ca3af' }}>•</span>
          <span style={{ color: '#d1d5db' }}>
            {currentPhase === 'bidding' ? '💰 Bidding' : 
             currentPhase === 'playing' ? '🃏 Playing' : 
             '⏳ Waiting'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default GameInterfaceEnhancement;
